import { createApi } from "@reduxjs/toolkit/query/react"

import { ApiTypes } from "types"

import { baseQueryWithAuth } from "utils/api"
import { prepareQueryParams } from "utils/prepareQueryParams"

export const widgetsApi = createApi({
  reducerPath: "widgets",
  tagTypes: ["widgets"],
  baseQuery: baseQueryWithAuth,
  endpoints: builder => ({
    getInventory: builder.query<{ data: ApiTypes.Res.InventoryWidget }, void>({
      query: () => ({
        url: `/dashboard/widgets/inventory`,
        method: "GET",
      }),
    }),
    getMonitoring: builder.query<{ data: ApiTypes.Res.MonitoringWidget }, void>({
      query: () => ({
        url: `/dashboard/widgets/monitoring`,
        method: "GET",
      }),
    }),
    getStatus: builder.query<{ data: ApiTypes.Res.StatusWidget }, void>({
      query: () => ({
        url: `/dashboard/widgets/status`,
        method: "GET",
      }),
    }),
    getPerftestResults: builder.query<
      { data: ApiTypes.Res.PerftestResultsWidget },
      { date_start?: string; date_end?: string } | void
    >({
      query: params => ({
        url: `/dashboard/widgets/perftest-results`,
        method: "GET",
        ...(params && Object.keys(params).length > 0 && { params }),
      }),
    }),
    getPerftestStatuses: builder.query<{ data: ApiTypes.Res.PerftestStatusesWidget }, void>({
      query: () => ({
        url: `/dashboard/widgets/perftest-statuses`,
        method: "GET",
      }),
    }),
    getAlertTypes: builder.query<
      { data: ApiTypes.Res.AlertTypesWidget },
      { date_start?: string; date_end?: string } | void
    >({
      query: params => ({
        url: `/dashboard/widgets/alert-types`,
        method: "GET",
        params: prepareQueryParams(params),
      }),
    }),
    getAlertStatuses: builder.query<
      { data: ApiTypes.Res.AlertStatusesWidget },
      { date_start?: string; date_end?: string } | void
    >({
      query: params => ({
        url: `/dashboard/widgets/alert-statuses`,
        method: "GET",
        params: prepareQueryParams(params),
      }),
    }),
    getOrderProducts: builder.query<
      { data: ApiTypes.Res.OrderProductsWidget },
      { date_start?: string; date_end?: string } | void
    >({
      query: params => ({
        url: `/dashboard/widgets/order-products`,
        method: "GET",
        params: prepareQueryParams(params),
      }),
    }),
    getOrderStatuses: builder.query<{ data: ApiTypes.Res.ContractorOrderStatusesWidget }, void>({
      query: () => ({
        url: `/dashboard/widgets/order-statuses`,
        method: "GET",
      }),
    }),
    getInstalledDevices: builder.query<
      { data: ApiTypes.Res.InstalledDevicesWidget },
      { date_start?: string; date_end?: string } | void
    >({
      query: params => ({
        url: `/dashboard/widgets/installed-devices`,
        method: "GET",
        params: params ? prepareQueryParams(params) : undefined,
      }),
    }),
    getTotalStats: builder.query<{ data: ApiTypes.Res.TotalStatsWidget }, void>({
      query: () => ({
        url: `/dashboard/widgets/total-stats`,
        method: "GET",
      }),
      keepUnusedDataFor: 0,
    }),
    getCustomersAppsTrend: builder.query<{ data: ApiTypes.Res.CustomersAppsTrendWidget }, { period: number }>({
      query: params => ({
        url: `/dashboard/widgets/customers-apps-trend`,
        method: "GET",
        params: prepareQueryParams(params),
      }),
      keepUnusedDataFor: 3600,
    }),
    getInstallationDevicesTrend: builder.query<
      { data: ApiTypes.Res.InstallationDevicesTrendWidget },
      { period: number }
    >({
      query: params => ({
        url: `/dashboard/widgets/installation-devices-trend`,
        method: "GET",
        params: prepareQueryParams(params),
      }),
      keepUnusedDataFor: 3600,
    }),
    getSystemHealthTrend: builder.query<{ data: ApiTypes.Res.SystemHealthTrendWidget }, { period: number }>({
      query: params => ({
        url: `/dashboard/widgets/system-health-trend`,
        method: "GET",
        params: prepareQueryParams(params),
      }),
      keepUnusedDataFor: 3600,
    }),
  }),
})

export const {
  useLazyGetInventoryQuery,
  useLazyGetMonitoringQuery,
  useLazyGetStatusQuery,
  useLazyGetPerftestResultsQuery,
  useLazyGetPerftestStatusesQuery,
  useLazyGetAlertTypesQuery,
  useLazyGetAlertStatusesQuery,
  useLazyGetOrderProductsQuery,
  useLazyGetOrderStatusesQuery,
  useLazyGetInstalledDevicesQuery,
  useGetTotalStatsQuery,
  useLazyGetCustomersAppsTrendQuery,
  useLazyGetInstallationDevicesTrendQuery,
  useLazyGetSystemHealthTrendQuery,
} = widgetsApi
